import { createApi } from '@reduxjs/toolkit/query/react';

import {
  RecordsEndpoints as Endpoints,
  type CreateRecordByAiRequestIdRequest,
  type CreateRecordByAiRequestIdResponse,
  type DeleteRecordsRequest,
  type DeleteRecordsResponse,
  type DeleteRecordFromJournalRequest,
  type DeleteRecordFromJournalResponse,
  type ExportRecordsByChainToExcelRequest,
  type ExportRecordsByChainToExcelResponse,
  type ExportRecordsByOrganizationToExcelRequest,
  type ExportRecordsByOrganizationToExcelResponse,
  type ExportRecordsToExcelByIdsRequest,
  type ExportRecordsToExcelByIdsResponse,
  type ExportRecordsToExcelRequest,
  type ExportRecordsToExcelResponse,
  type ExportRecordsToExcelWithoutGroupingRequest,
  type ExportRecordsToExcelWithoutGroupingResponse,
  type GetEmployeeRecordsRequest,
  type GetEmployeeRecordsResponse,
  type GetEmployeesRecordsRequest,
  type GetEmployeesRecordsResponse,
  type GetRecordsJournalLoadRequest,
  type GetRecordsJournalLoadResponse,
  type GetEmployeesRecordsOnDatesRequest,
  type GetEmployeesRecordsOnDatesResponse,
  type GetEmployeesByServicesRequest,
  type GetEmployeesByServicesResponse,
  type GetEmployeeRecordsTipRequest,
  type GetEmployeeRecordsTipResponse,
  type GetClientRecordsRequest,
  type GetClientRecordsResponse,
  type GetRecordReceiptPdfRequest,
  type GetRecordReceiptPdfResponse,
  type GetRecordHistoryRequest,
  type GetRecordHistoryResponse,
  type GetRecordPaymentsRequest,
  type GetRecordPaymentsResponse,
  type GetRecordByIdRequest,
  type GetRecordByIdResponse,
  type GetRecordFilterListsRequest,
  type GetRecordFilterListsResponse,
  type GetRecordsSearchResultRequest,
  type GetRecordsSearchResultResponse,
  type GetRecordsSearchResultFullRequest,
  type GetRecordsSearchResultFullResponse,
  type ImportExcelRequest,
  type ImportExcelResponse,
  type PrepareRecordCreationRequest,
  type PrepareRecordCreationResponse,
  type CheckRecordMoveRequest,
  type CheckRecordMoveResponse,
  type CheckRecordTimesRequest,
  type CheckRecordTimesResponse,
  type SearchRecordsRequest,
  type SearchRecordsResponse,
  type GetRecordPropertiesRequest,
  type GetRecordPropertiesResponse,
  type SetRecordPropertiesRequest,
  type SetRecordPropertiesResponse,
  type SetRecordsTagsRequest,
  type SetRecordsTagsResponse,
  type StopImportExcelRequest,
  type StopImportExcelResponse,
  type UpdateRecordRequest,
  type UpdateRecordResponse,
} from '@app/api/Records/types';
import { createApiBaseQuery, Methods } from '@app/api/shared';
import { RECORDS_RESOURCE } from '@app/constants';

export const recordsApi2 = createApi({
  reducerPath: 'recordsApi2',
  baseQuery: createApiBaseQuery(RECORDS_RESOURCE),
  endpoints: ({ mutation, query }) => ({
    [Endpoints.GetRecordFilterLists]: query<
      GetRecordFilterListsResponse,
      GetRecordFilterListsRequest
    >({
      query: ({ logs, ...params }) => ({
        url: Endpoints.GetRecordFilterLists,
        method: Methods.GET,
        params,
        logs,
      }),
    }),
    [Endpoints.SearchRecords]: mutation<
      SearchRecordsResponse,
      SearchRecordsRequest
    >({
      query: ({ data, logs, ...params }) => ({
        url: Endpoints.SearchRecords,
        method: Methods.POST,
        params,
        data,
        logs,
      }),
    }),
    [Endpoints.GetRecordsSearchResult]: query<
      GetRecordsSearchResultResponse,
      GetRecordsSearchResultRequest
    >({
      query: (params) => ({
        url: Endpoints.GetRecordsSearchResult,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetRecordsSearchResultFull]: query<
      GetRecordsSearchResultFullResponse,
      GetRecordsSearchResultFullRequest
    >({
      query: (params) => ({
        url: Endpoints.GetRecordsSearchResultFull,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetRecordById]: query<
      GetRecordByIdResponse,
      GetRecordByIdRequest
    >({
      query: (params) => ({
        url: Endpoints.GetRecordById,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.ExportRecordsToExcel]: query<
      ExportRecordsToExcelResponse,
      ExportRecordsToExcelRequest
    >({
      query: (params) => ({
        url: Endpoints.ExportRecordsToExcel,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.ExportRecordsToExcelWithoutGrouping]: query<
      ExportRecordsToExcelWithoutGroupingResponse,
      ExportRecordsToExcelWithoutGroupingRequest
    >({
      query: (params) => ({
        url: Endpoints.ExportRecordsToExcelWithoutGrouping,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.ExportRecordsToExcelByIds]: mutation<
      ExportRecordsToExcelByIdsResponse,
      ExportRecordsToExcelByIdsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.ExportRecordsToExcelByIds,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.DeleteRecords]: mutation<
      DeleteRecordsResponse,
      DeleteRecordsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.DeleteRecords,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.PrepareRecordCreation]: query<
      PrepareRecordCreationResponse,
      PrepareRecordCreationRequest
    >({
      query: (params) => ({
        url: Endpoints.PrepareRecordCreation,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.UpdateRecord]: mutation<
      UpdateRecordResponse,
      UpdateRecordRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateRecord,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.CheckRecordTimes]: query<
      CheckRecordTimesResponse,
      CheckRecordTimesRequest
    >({
      query: (params) => ({
        url: Endpoints.CheckRecordTimes,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.CheckRecordMove]: mutation<
      CheckRecordMoveResponse,
      CheckRecordMoveRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.CheckRecordMove,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetRecordHistory]: query<
      GetRecordHistoryResponse,
      GetRecordHistoryRequest
    >({
      query: (params) => ({
        url: Endpoints.GetRecordHistory,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetEmployeesRecords]: query<
      GetEmployeesRecordsResponse,
      GetEmployeesRecordsRequest
    >({
      query: ({ logs, ...params }) => ({
        url: Endpoints.GetEmployeesRecords,
        method: Methods.GET,
        params,
        logs,
      }),
    }),
    [Endpoints.GetRecordsJournalLoad]: query<
      GetRecordsJournalLoadResponse,
      GetRecordsJournalLoadRequest
    >({
      query: (params) => ({
        url: Endpoints.GetRecordsJournalLoad,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetEmployeeRecords]: query<
      GetEmployeeRecordsResponse,
      GetEmployeeRecordsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetEmployeeRecords,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetEmployeesRecordsOnDates]: mutation<
      GetEmployeesRecordsOnDatesResponse,
      GetEmployeesRecordsOnDatesRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.GetEmployeesRecordsOnDates,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetEmployeeRecordsTip]: query<
      GetEmployeeRecordsTipResponse,
      GetEmployeeRecordsTipRequest
    >({
      query: (params) => ({
        url: Endpoints.GetEmployeeRecordsTip,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.DeleteRecordFromJournal]: mutation<
      DeleteRecordFromJournalResponse,
      DeleteRecordFromJournalRequest
    >({
      query: (params) => ({
        url: Endpoints.DeleteRecordFromJournal,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.GetEmployeesByServices]: mutation<
      GetEmployeesByServicesResponse,
      GetEmployeesByServicesRequest
    >({
      query: ({ data, logs, ...params }) => ({
        url: Endpoints.GetEmployeesByServices,
        method: Methods.POST,
        params,
        data,
        logs,
      }),
    }),
    [Endpoints.SetRecordsTags]: mutation<
      SetRecordsTagsResponse,
      SetRecordsTagsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SetRecordsTags,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetRecordProperties]: query<
      GetRecordPropertiesResponse,
      GetRecordPropertiesRequest
    >({
      query: (params) => ({
        url: Endpoints.GetRecordProperties,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SetRecordProperties]: mutation<
      SetRecordPropertiesResponse,
      SetRecordPropertiesRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SetRecordProperties,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetClientRecords]: query<
      GetClientRecordsResponse,
      GetClientRecordsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientRecords,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetRecordReceiptPdf]: query<
      GetRecordReceiptPdfResponse,
      GetRecordReceiptPdfRequest
    >({
      query: (params) => ({
        url: Endpoints.GetRecordReceiptPdf,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetRecordPayments]: query<
      GetRecordPaymentsResponse,
      GetRecordPaymentsRequest
    >({
      query: ({ recordIds, ActiveBranchId }) => {
        const queryParams = new URLSearchParams();
        queryParams.set('ActiveBranchId', String(ActiveBranchId));
        recordIds.forEach((id) => queryParams.append('recordIds', String(id)));

        return {
          url: `${Endpoints.GetRecordPayments}?${queryParams.toString()}`,
          method: Methods.GET,
        };
      },
    }),
    [Endpoints.ExportRecordsByChainToExcel]: mutation<
      ExportRecordsByChainToExcelResponse,
      ExportRecordsByChainToExcelRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.ExportRecordsByChainToExcel,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.ExportRecordsByOrganizationToExcel]: mutation<
      ExportRecordsByOrganizationToExcelResponse,
      ExportRecordsByOrganizationToExcelRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.ExportRecordsByOrganizationToExcel,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.ImportExcel]: mutation<ImportExcelResponse, ImportExcelRequest>({
      query: ({ data, ...params }) => ({
        url: Endpoints.ImportExcel,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.StopImportExcel]: mutation<
      StopImportExcelResponse,
      StopImportExcelRequest
    >({
      query: (params) => ({
        url: Endpoints.StopImportExcel,
        method: Methods.DELETE,
        params,
      }),
    }),
    [Endpoints.CreateRecordByAiRequestId]: mutation<
      CreateRecordByAiRequestIdResponse,
      CreateRecordByAiRequestIdRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.CreateRecordByAiRequestId,
        method: Methods.POST,
        params,
        data,
      }),
    }),
  }),
});

export const {
  useGetRecordFilterListsQuery,
  useLazyGetRecordFilterListsQuery,
  useSearchRecordsMutation,
  useGetRecordsSearchResultQuery,
  useLazyGetRecordsSearchResultQuery,
  useGetRecordsSearchResultFullQuery,
  useLazyGetRecordsSearchResultFullQuery,
  useGetRecordByIdQuery,
  useLazyGetRecordByIdQuery,
  useExportRecordsToExcelQuery,
  useLazyExportRecordsToExcelQuery,
  useExportRecordsToExcelWithoutGroupingQuery,
  useLazyExportRecordsToExcelWithoutGroupingQuery,
  useExportRecordsToExcelByIdsMutation,
  useDeleteRecordsMutation,
  usePrepareRecordCreationQuery,
  useLazyPrepareRecordCreationQuery,
  useUpdateRecordMutation,
  useCheckRecordTimesQuery,
  useLazyCheckRecordTimesQuery,
  useCheckRecordMoveMutation,
  useGetRecordHistoryQuery,
  useLazyGetRecordHistoryQuery,
  useGetEmployeesRecordsQuery,
  useLazyGetEmployeesRecordsQuery,
  useGetRecordsJournalLoadQuery,
  useLazyGetRecordsJournalLoadQuery,
  useGetEmployeeRecordsQuery,
  useLazyGetEmployeeRecordsQuery,
  useGetEmployeesRecordsOnDatesMutation,
  useGetEmployeeRecordsTipQuery,
  useLazyGetEmployeeRecordsTipQuery,
  useDeleteRecordFromJournalMutation,
  useGetEmployeesByServicesMutation,
  useSetRecordsTagsMutation,
  useLazyGetRecordPropertiesQuery,
  useSetRecordPropertiesMutation,
  useGetClientRecordsQuery,
  useLazyGetClientRecordsQuery,
  useGetRecordReceiptPdfQuery,
  useLazyGetRecordReceiptPdfQuery,
  useGetRecordPaymentsQuery,
  useLazyGetRecordPaymentsQuery,
  useExportRecordsByChainToExcelMutation,
  useExportRecordsByOrganizationToExcelMutation,
  useImportExcelMutation,
  useStopImportExcelMutation,
  useCreateRecordByAiRequestIdMutation,
} = recordsApi2;
